import { useState } from "react";
import { ChevronDown, ChevronRight, ExternalLink, Check, X } from "lucide-react";

const ENGINE_META = {
  chatgpt: { label: "ChatGPT", color: "border-emerald-300", dot: "bg-emerald-500" },
  claude: { label: "Claude", color: "border-orange-300", dot: "bg-orange-500" },
  gemini: { label: "Gemini", color: "border-blue-300", dot: "bg-blue-500" },
  perplexity: { label: "Perplexity", color: "border-sky-300", dot: "bg-sky-500" },
};
const SENTIMENT_TONE = {
  positive: "bg-emerald-50 text-emerald-700 border-emerald-200",
  neutral: "bg-slate-50 text-slate-600 border-slate-200",
  negative: "bg-rose-50 text-rose-700 border-rose-200",
  mixed: "bg-amber-50 text-amber-700 border-amber-200",
};

/**
 * PromptResponsesBlock — for ONE prompt, renders the 4 engine answers in full
 * with their citations and extracted structured data. This is the "full caption"
 * view the user asked for: nothing is summarised, every citation link is shown,
 * every engine response is visible verbatim.
 *
 * Props:
 *   prompt: { text, prompt_type, label? }
 *   rows: array of result rows (one per engine that ran this prompt)
 *   defaultOpen: boolean — expand by default
 */
export default function PromptResponsesBlock({ prompt, rows, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const byEngine = {};
  for (const r of rows) byEngine[r.engine] = r;
  const engines = ["chatgpt", "claude", "gemini", "perplexity"];

  // Compact summary: how many engines mentioned/recognized the brand.
  const isBrand = prompt.prompt_type === "brand";
  const positives = rows.filter((r) => {
    const s = r.structured || {};
    return r.ok && (isBrand ? s.brand_mentioned : s.brand_appeared);
  }).length;

  return (
    <div
      className="rounded-md border border-slate-200 bg-white overflow-hidden"
      data-testid={`geo-prompt-block-${prompt.prompt_type}`}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-slate-50 text-left"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 text-slate-500 shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-slate-500 shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          {prompt.label && (
            <div className="text-[10px] uppercase tracking-wider text-violet-700 font-medium mb-0.5">
              {prompt.label}
            </div>
          )}
          <div className="text-sm font-medium text-slate-800 leading-snug">
            {prompt.text}
          </div>
        </div>
        <span className="shrink-0 text-xs text-slate-500">
          {positives}/{rows.length} {isBrand ? "reconocieron" : "mencionaron"}
        </span>
      </button>

      {open && (
        <div className="border-t border-slate-100 grid grid-cols-1 lg:grid-cols-2 gap-3 p-3 bg-slate-50/40">
          {engines.map((eng) => {
            const r = byEngine[eng];
            if (!r) return null;
            return <EngineResponseCard key={eng} engine={eng} row={r} isBrand={isBrand} />;
          })}
        </div>
      )}
    </div>
  );
}

function EngineResponseCard({ engine, row, isBrand }) {
  const meta = ENGINE_META[engine];
  const s = row.structured || {};
  const mentioned = isBrand ? s.brand_mentioned : s.brand_appeared;
  const sentiment = (s.sentiment || "").toLowerCase();

  return (
    <div
      className={`rounded-md border-2 ${meta.color} bg-white overflow-hidden`}
      data-testid={`geo-response-${engine}`}
    >
      {/* HEADER */}
      <div className="px-3 py-2 bg-white border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`h-2 w-2 rounded-full ${meta.dot}`} />
          <span className="text-sm font-semibold text-slate-800">{meta.label}</span>
          {row.ok ? (
            mentioned ? (
              <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-medium">
                <Check className="h-3 w-3" />
                {isBrand ? "Reconocido" : `Apareció${s.position ? ` · #${s.position}` : ""}`}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                <X className="h-3 w-3" />
                {isBrand ? "No reconocido" : "No apareció"}
              </span>
            )
          ) : (
            <span className="text-[11px] px-1.5 py-0.5 rounded bg-rose-50 text-rose-700">error</span>
          )}
        </div>
        {row.duration_ms != null && (
          <span className="text-[10px] text-slate-400">{(row.duration_ms / 1000).toFixed(1)}s</span>
        )}
      </div>

      {/* ANSWER — FULL caption, no truncation */}
      <div className="px-3 py-3 border-b border-slate-100">
        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-1">
          Respuesta completa
        </div>
        {!row.ok ? (
          <div className="text-xs text-rose-600 italic">{row.error || "Error sin detalle"}</div>
        ) : (
          <div className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap max-h-[420px] overflow-y-auto custom-scroll">
            {row.answer}
          </div>
        )}
      </div>

      {/* STRUCTURED EXTRACT */}
      {row.ok && (
        <div className="px-3 py-2.5 space-y-2.5 bg-slate-50/40">
          {/* sentiment for brand */}
          {isBrand && sentiment && (
            <KV label="Sentiment">
              <span
                className={`text-[11px] px-1.5 py-0.5 rounded border ${SENTIMENT_TONE[sentiment] || SENTIMENT_TONE.neutral}`}
              >
                {sentiment}
              </span>
            </KV>
          )}

          {/* position for category */}
          {!isBrand && s.position != null && (
            <KV label="Posición">
              <span className="text-xs font-medium text-slate-700">#{s.position}</span>
            </KV>
          )}

          {/* context_snippet */}
          {s.context_snippet && (
            <KV label="Cómo me cita">
              <span className="text-xs text-slate-700 italic">{`"${s.context_snippet}"`}</span>
            </KV>
          )}

          {/* description_snippet for brand */}
          {isBrand && s.description_snippet && (
            <KV label="Cómo me describe">
              <span className="text-xs text-slate-700 italic">{`"${s.description_snippet}"`}</span>
            </KV>
          )}

          {/* attributes for brand */}
          {isBrand && Array.isArray(s.attributes) && s.attributes.length > 0 && (
            <KV label="Atributos">
              <div className="flex flex-wrap gap-1">
                {s.attributes.map((a, i) => (
                  <span
                    key={i}
                    className="text-[11px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-100"
                  >
                    {a}
                  </span>
                ))}
              </div>
            </KV>
          )}

          {/* factual errors for brand */}
          {isBrand && Array.isArray(s.factual_errors) && s.factual_errors.length > 0 && (
            <KV label="Errores factuales">
              <ul className="list-disc list-inside text-xs text-rose-700 space-y-0.5">
                {s.factual_errors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </KV>
          )}

          {/* competitors mentioned (brand) or competitors ranked (category) */}
          {((isBrand && Array.isArray(s.competitors_mentioned) && s.competitors_mentioned.length > 0) ||
            (!isBrand && Array.isArray(s.competitors) && s.competitors.length > 0)) && (
            <KV label="Competidores nombrados">
              <ul className="text-xs text-slate-700 space-y-1">
                {(isBrand ? s.competitors_mentioned : s.competitors).map((c, i) => (
                  <li key={i} className="leading-snug">
                    {!isBrand && c.position != null && (
                      <span className="text-slate-400 font-medium mr-1">#{c.position}</span>
                    )}
                    <span className="font-medium">{c.name}</span>
                    {c.context && <span className="text-slate-500"> — {c.context}</span>}
                  </li>
                ))}
              </ul>
            </KV>
          )}

          {/* citations / sources — clickable links */}
          {Array.isArray(s.citations) && s.citations.length > 0 && (
            <KV label="Fuentes citadas">
              <ul className="space-y-1">
                {s.citations.map((c, i) => {
                  const url = (c && typeof c === "object" ? c.url : c) || "";
                  const title = (c && typeof c === "object" ? c.title : "") || "";
                  let host = "";
                  try { host = url ? new URL(url).hostname : ""; } catch { /* noop */ }
                  return (
                    <li key={i} className="text-xs">
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-slate-700 hover:text-violet-700 hover:underline"
                      >
                        <ExternalLink className="h-3 w-3 shrink-0 text-slate-400" />
                        <span className="font-medium">{title || host || url}</span>
                        {host && title && <span className="text-slate-400"> · {host}</span>}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </KV>
          )}
        </div>
      )}
    </div>
  );
}

function KV({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-1">{label}</div>
      {children}
    </div>
  );
}
